// ============================================================================
// Anomaly detectors — rule-based checks returning typed findings.
// Each detector is pure and independently testable; runAllDetectors composes
// them. Severity uses the reserved status scale (never series colors).
// ============================================================================

import { PaymentRecord } from '../../services/paymentService';
import { daysBetween, getSeasonEnd, getSeasonStart } from '../dateUtils';
import { groupBy, percentile } from './aggregations';
import { AnalysisStay, AnomalyFinding, isRevenueStay } from './types';
import { isSuccessfulPayment } from './paymentMetrics';

export interface AnomalyInput {
  stays: AnalysisStay[];
  payments: PaymentRecord[];
  today: string; // YYYY-MM-DD
  /** Stays longer than this many days are flagged (fallback when p99 is low). */
  longStayThresholdDays?: number;
  /** Days a confirmed reservation may sit unpaid before being flagged. */
  staleUnpaidDays?: number;
  /** Expected gateway environment in production (e.g. 'production'). */
  expectedGatewayEnvironment?: string;
}

function finding(
  severity: AnomalyFinding['severity'],
  type: string,
  stay: Pick<AnalysisStay, 'id' | 'confirmationCode'>,
  message: string
): AnomalyFinding {
  return { severity, type, reservationId: stay.id, confirmationCode: stay.confirmationCode, message };
}

/** Bookings whose dates fall outside the current season/year window. */
export function detectOutOfSeasonBookings(stays: AnalysisStay[], today: string): AnomalyFinding[] {
  const year = Number(today.slice(0, 4));
  const results: AnomalyFinding[] = [];
  for (const stay of stays) {
    if (!isRevenueStay(stay)) continue;
    const checkInYear = Number(stay.checkIn.slice(0, 4));
    if (checkInYear > year + 1) {
      results.push(finding('serious', 'far_future_booking', stay,
        `Check-in ${stay.checkIn} is more than a year out`));
    } else if (checkInYear < year && stay.status === 'confirmed') {
      results.push(finding('warning', 'past_year_booking', stay,
        `Confirmed booking with past-year check-in ${stay.checkIn}`));
    } else if (checkInYear === year) {
      const seasonStart = getSeasonStart(year);
      const seasonEnd = getSeasonEnd(year);
      if (stay.checkIn < seasonStart || stay.checkIn > seasonEnd) {
        results.push(finding('info', 'off_season_booking', stay,
          `Check-in ${stay.checkIn} is outside the ${seasonStart} – ${seasonEnd} season`));
      }
    }
  }
  return results;
}

/** Same guest with overlapping stays on the same site. */
export function detectDuplicateBookings(stays: AnalysisStay[]): AnomalyFinding[] {
  const active = stays.filter(isRevenueStay).filter(s => s.memberId || s.customerId);
  const byGuest = groupBy(active, s => s.memberId ? `m${s.memberId}` : `c${s.customerId}`);
  const results: AnomalyFinding[] = [];
  const seen = new Set<string>();

  byGuest.forEach(guestStays => {
    if (guestStays.length < 2) return;
    for (let i = 0; i < guestStays.length; i++) {
      for (let j = i + 1; j < guestStays.length; j++) {
        const a = guestStays[i];
        const b = guestStays[j];
        if (a.id === b.id) continue;
        const overlap = a.checkIn < b.checkOut && b.checkIn < a.checkOut;
        if (!overlap) continue;
        const sharedSite = a.sites.some(sa =>
          sa.status === 'active' &&
          b.sites.some(sb => sb.status === 'active' && sb.siteId === sa.siteId)
        );
        if (sharedSite) {
          const key = [a.id, b.id].sort((x, y) => x - y).join('-');
          if (seen.has(key)) continue;
          seen.add(key);
          results.push(finding('serious', 'duplicate_booking', a,
            `Overlaps reservation ${b.confirmationCode || b.id} for the same guest and site`));
        }
      }
    }
  });
  return results;
}

/** Confirmed reservations with zero or negative cost. */
export function detectZeroCostReservations(stays: AnalysisStay[]): AnomalyFinding[] {
  return stays
    .filter(isRevenueStay)
    .filter(s => s.cost <= 0)
    .map(s => finding(s.cost < 0 ? 'critical' : 'warning', 'zero_cost', s,
      s.cost < 0 ? `Negative cost ${s.cost}` : 'Zero-cost confirmed reservation'));
}

/** Stays longer than the threshold (raised to the dataset's p99 when there
 * is enough data for p99 to be meaningful). */
export function detectLongStays(stays: AnalysisStay[], thresholdDays = 28): AnomalyFinding[] {
  const revenueStays = stays.filter(isRevenueStay);
  const dayCounts = revenueStays.map(s => s.days);
  const p99 = dayCounts.length >= 50 ? percentile(dayCounts, 99) : 0;
  const cutoff = Math.max(thresholdDays, Math.ceil(p99));
  return revenueStays
    .filter(s => s.days > cutoff)
    .map(s => finding('info', 'long_stay', s, `${s.days}-night stay exceeds the ${cutoff}-night threshold`));
}

/** A cancellation followed by a new booking for the same guest+site+date within a day. */
export function detectCancelRebook(stays: AnalysisStay[]): AnomalyFinding[] {
  const cancelled = stays.filter(s => s.status === 'cancelled' && (s.memberId || s.customerId));
  const results: AnomalyFinding[] = [];
  for (const cancelledStay of cancelled) {
    const cancelledAt = cancelledStay.sites.find(site => site.cancelledAt)?.cancelledAt;
    if (!cancelledAt) continue;
    const rebooked = stays.find(candidate =>
      candidate.id !== cancelledStay.id &&
      isRevenueStay(candidate) &&
      candidate.checkIn === cancelledStay.checkIn &&
      ((candidate.memberId && candidate.memberId === cancelledStay.memberId) ||
        (candidate.customerId && candidate.customerId === cancelledStay.customerId)) &&
      candidate.createdAt !== undefined &&
      Math.abs(new Date(candidate.createdAt).getTime() - new Date(cancelledAt).getTime()) < 24 * 36e5 &&
      candidate.sites.some(sa =>
        sa.status === 'active' &&
        cancelledStay.sites.some(sb => sb.siteId === sa.siteId))
    );
    if (rebooked) {
      results.push(finding('warning', 'cancel_rebook', cancelledStay,
        `Cancelled then rebooked as ${rebooked.confirmationCode || rebooked.id} within 24h (possible rate manipulation)`));
    }
  }
  return results;
}

/** is_current_version chains that point at rows missing from the dataset. */
export function detectOrphanVersions(stays: AnalysisStay[]): AnomalyFinding[] {
  const ids = new Set(stays.map(s => s.id));
  const results: AnomalyFinding[] = [];
  for (const stay of stays) {
    if (stay.status === 'superseded' && stay.isCurrentVersion) {
      results.push(finding('serious', 'orphan_version', stay,
        'Superseded reservation still flagged as current version'));
    }
    if (
      !stay.isCurrentVersion &&
      stay.supersededByReservationId !== undefined &&
      !ids.has(stay.supersededByReservationId)
    ) {
      results.push(finding('info', 'orphan_version', stay,
        `Superseded by reservation ${stay.supersededByReservationId}, which is not in the loaded window`));
    }
  }
  return results;
}

/** Confirmed reservations with no successful payment after N days. */
export function detectStaleUnpaid(
  stays: AnalysisStay[],
  payments: PaymentRecord[],
  today: string,
  staleDays = 7
): AnomalyFinding[] {
  const paidReservations = new Set(
    payments.filter(isSuccessfulPayment).map(p => p.reservation_id).filter(Boolean)
  );
  const results: AnomalyFinding[] = [];
  for (const stay of stays) {
    if (!isRevenueStay(stay) || stay.status !== 'confirmed' || !stay.createdAt) continue;
    if (paidReservations.has(String(stay.id))) continue;
    const age = daysBetween(stay.createdAt.split('T')[0], today);
    if (age > staleDays) {
      results.push(finding('warning', 'stale_unpaid', stay,
        `Confirmed ${age} days ago with no successful payment on record`));
    }
  }
  return results;
}

/** Payments recorded against a non-expected gateway environment. */
export function detectNonProductionGateway(
  payments: PaymentRecord[],
  expectedEnvironment = 'production'
): AnomalyFinding[] {
  return payments
    .filter(p => p.gateway_environment && p.gateway_environment !== expectedEnvironment && p.gateway_provider !== 'manual')
    .map(p => ({
      severity: 'critical' as const,
      type: 'gateway_environment',
      reservationId: Number(p.reservation_id) || 0,
      confirmationCode: '',
      message: `Payment ${p.id} recorded in '${p.gateway_environment}' environment via ${p.gateway_provider}`,
    }));
}

const SEVERITY_ORDER: Record<AnomalyFinding['severity'], number> = {
  critical: 0,
  serious: 1,
  warning: 2,
  info: 3,
};

export function runAllDetectors(input: AnomalyInput): AnomalyFinding[] {
  const findings = [
    ...detectNonProductionGateway(input.payments, input.expectedGatewayEnvironment),
    ...detectZeroCostReservations(input.stays),
    ...detectDuplicateBookings(input.stays),
    ...detectOrphanVersions(input.stays),
    ...detectOutOfSeasonBookings(input.stays, input.today),
    ...detectCancelRebook(input.stays),
    ...detectStaleUnpaid(input.stays, input.payments, input.today, input.staleUnpaidDays),
    ...detectLongStays(input.stays, input.longStayThresholdDays),
  ];
  return findings.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
}
